import { useEffect, useState } from 'react';
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CheckCircle2,
  Eye,
  EyeOff,
  Lock,
  Network,
  ShieldCheck,
  Sparkles,
  User,
  UsersRound,
  Zap,
} from 'lucide-react';
import { loginPortal } from '../../services/authService';

function RcmLogo() {
  return (
    <svg
      className="h-14 w-14 lg:h-20 lg:w-20"
      fill="none"
      height="80"
      viewBox="0 0 120 120"
      width="80"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M38 82V42L50 34V82"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="6"
      />
      <path
        d="M60 82V24L72 16V82"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="6"
      />
      <path
        d="M82 82V52L94 44V82"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="6"
      />
      <path
        d="M28 92H102"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="6"
      />
    </svg>
  );
}

function LoginPage({ onLoginSuccess, portal = 'superadmin', sessionMessage = '' }) {
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const portalLabels = {
    superadmin: 'Super Admin Access',
    admin: 'Admin Access',
    staff: 'Staff Access',
  };
  const heading = portalLabels[portal] || 'Portal Access';
  const isStaffPortal = portal === 'staff';

  useEffect(() => {
    const animationFrame = window.requestAnimationFrame(() => {
      setIsVisible(true);
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await loginPortal(form, portal);
      onLoginSuccess({
        token: data.token,
        user: data.user,
      });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const visibilityClass = isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0';
  const mobileVisibilityClass = isVisible ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0';
  const inputClass =
    'w-full rounded-[14px] border border-[#ded4c8] bg-[#fbf8f3] px-4 py-2.5 pl-11 text-[0.9rem] font-semibold text-[#172033] outline-none transition placeholder:text-slate-400 hover:border-[#c8b39e] focus:border-[#6B3A1E] focus:bg-white focus:shadow-[0_0_0_4px_rgba(107,58,30,0.14)] lg:py-3 lg:text-[0.92rem]';
  const mobileInputClass =
    'h-14 w-full rounded-2xl border border-[#ded4c8] bg-white pl-12 pr-4 text-[0.95rem] font-semibold text-slate-800 outline-none transition-all duration-300 placeholder:text-slate-400 focus:border-[#6B3A1E] focus:ring-4 focus:ring-[#eadccf]';
  const mobileFeatureCards = [
    { label: 'Multi Branch', detail: 'Management', icon: Building2 },
    { label: 'Secure', detail: 'Platform', icon: ShieldCheck },
    { label: 'Real Time', detail: 'Insights', icon: Zap },
  ];
  const desktopFeatureCards = [
    {
      title: 'Tenant Operations',
      description: 'Monitor performance and manage all tenant activities.',
      icon: Building2,
    },
    {
      title: 'User Administration',
      description: 'Manage user roles, permissions and access levels.',
      icon: UsersRound,
    },
    {
      title: 'Branch Coordination',
      description: 'Streamline communication and operations across branches.',
      icon: Network,
    },
  ];

  const renderPasswordToggle = () => (
    <button
      aria-label={showPassword ? 'Hide password' : 'Show password'}
      className="absolute right-3 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-slate-500 transition hover:bg-[#e9eef5] hover:text-[#172033]"
      onClick={() => setShowPassword((currentValue) => !currentValue)}
      type="button"
    >
      {showPassword ? (
        <EyeOff className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Eye className="h-4 w-4" aria-hidden="true" />
      )}
    </button>
  );

  const renderMobilePasswordToggle = () => (
    <button
      aria-label={showPassword ? 'Hide password' : 'Show password'}
      className="absolute right-4 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-slate-500 transition hover:bg-[#f3eadf] hover:text-[#5A321E]"
      onClick={() => setShowPassword((currentValue) => !currentValue)}
      type="button"
    >
      {showPassword ? (
        <EyeOff className="h-5 w-5" aria-hidden="true" />
      ) : (
        <Eye className="h-5 w-5" aria-hidden="true" />
      )}
    </button>
  );

  const renderLoginFields = ({ usernameLabel = 'Username', usernamePlaceholder = 'Enter username' } = {}) => (
    <div className="grid gap-3.5">
      <div>
        <label className="mb-1.5 block text-[0.82rem] font-semibold text-[#29364d]" htmlFor="username">
          {usernameLabel}
        </label>
        <div className="relative">
          <input
            className={inputClass}
            id="username"
            name="username"
            onChange={handleChange}
            placeholder={usernamePlaceholder}
            required
            type="text"
            value={form.username}
          />
          <User
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7a8aa0]"
          />
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-[0.82rem] font-semibold text-[#29364d]" htmlFor="password">
          Password
        </label>
        <div className="relative">
          <input
            className={`${inputClass} pr-12`}
            id="password"
            name="password"
            onChange={handleChange}
            placeholder="Enter password"
            required
            type={showPassword ? 'text' : 'password'}
            value={form.password}
          />
          <Lock
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7a8aa0]"
          />
          {renderPasswordToggle()}
        </div>
      </div>

      <button
        className="group mt-1 inline-flex min-h-[50px] w-full items-center justify-center gap-2 rounded-[14px] border border-[#3B1F12] bg-[#3B1F12] px-5 text-sm font-bold text-[#fff7ed] shadow-[0_14px_26px_rgba(75,42,24,0.22)] transition hover:-translate-y-0.5 hover:border-[#5A321E] hover:bg-[#5A321E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#c8a47c]/25 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
        disabled={loading}
        type="submit"
      >
        <span>{loading ? 'Signing in...' : 'Login'}</span>
        <ArrowRight
          aria-hidden="true"
          className="h-4 w-4 transition group-hover:translate-x-0.5"
        />
      </button>

      {sessionMessage ? (
        <p className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900">
          {sessionMessage}
        </p>
      ) : null}

      {error ? (
        <p className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
          {error}
        </p>
      ) : null}
    </div>
  );

  if (isStaffPortal) {
    return (
      <div className="min-h-screen bg-[#f7faf8] text-slate-950">
        <main className="relative grid min-h-screen overflow-hidden px-5 py-5 sm:px-8">
          <div className="absolute inset-x-0 top-0 h-44 bg-[linear-gradient(135deg,#0f766e_0%,#0891b2_46%,#f97316_100%)]" />
          <div className="absolute left-1/2 top-8 h-36 w-[min(560px,85vw)] -translate-x-1/2 rounded-full bg-white/25 blur-3xl" />

          <section className="relative m-auto w-full max-w-[410px]">
            <form
              className={`overflow-hidden rounded-[24px] border border-white/70 bg-white shadow-[0_20px_56px_rgba(15,23,42,0.14)] transition-all duration-500 ease-out ${visibilityClass}`}
              onSubmit={handleSubmit}
            >
              <div className="bg-slate-950 px-6 py-5 text-white sm:px-7">
                <div className="mb-5 flex items-center justify-between">
                  <div className="grid h-11 w-11 place-items-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                    <BadgeCheck className="h-5 w-5 text-cyan-200" aria-hidden="true" />
                  </div>
                  <span className="rounded-full border border-white/15 px-3 py-1 text-xs font-bold uppercase text-white/75">
                    Staff
                  </span>
                </div>
                <h1 className="text-2xl font-bold text-white">Welcome back</h1>
                <p className="mt-2 text-sm leading-6 text-slate-300">
                  Sign in to open your assigned workspace and continue daily operations.
                </p>
              </div>

              <div className="px-6 py-6 sm:px-7">
                {renderLoginFields({
                  usernameLabel: 'Staff ID',
                  usernamePlaceholder: 'Enter staff username',
                })}
              </div>
            </form>

            <div
              className={`mt-6 text-center text-xs font-semibold text-slate-500 transition-all delay-100 duration-500 ease-out ${visibilityClass}`}
            >
              <a
                href="https://zerooneitinc.com/"
                target="_blank"
                rel="noreferrer"
                className="transition-colors hover:text-[#6B3A1E]"
              >
                © 2026 ZeroOne IT Inc.
              </a>
              <span className="mx-2">|</span>
              <span>All rights reserved</span>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-[#172033]">
      <main className="relative min-h-screen overflow-hidden px-4 py-5 sm:px-5 lg:flex lg:items-center lg:justify-center lg:px-8 lg:py-6">
        <div className="pointer-events-none absolute -left-20 -top-24 h-72 w-72 rounded-full bg-white/0 blur-3xl lg:-left-32 lg:-top-32 lg:h-96 lg:w-96" />
        <div className="pointer-events-none absolute -bottom-24 -right-20 h-64 w-64 rounded-full bg-white/0 blur-3xl lg:-bottom-32 lg:-right-24 lg:h-96 lg:w-96" />
        <div className="pointer-events-none absolute -bottom-[170px] -left-[130px] hidden h-[350px] w-[470px] rounded-[50%] bg-gradient-to-br from-[#7A482B] via-[#5A321E] to-[#2F160B] opacity-92 lg:block" />
        <div className="pointer-events-none absolute -bottom-[148px] -left-[92px] hidden h-[250px] w-[370px] rounded-[50%] border border-[#dec4a9]/50 lg:block" />
        <div className="pointer-events-none absolute -bottom-[128px] -left-[58px] hidden h-[210px] w-[320px] rounded-[50%] border border-[#dec4a9]/38 lg:block" />
        <div className="pointer-events-none absolute -bottom-[108px] -left-[26px] hidden h-[175px] w-[270px] rounded-[50%] border border-[#dec4a9]/28 lg:block" />
        <div className="pointer-events-none absolute bottom-8 left-8 hidden grid-cols-6 gap-3 opacity-45 lg:grid">
          {Array.from({ length: 24 }).map((_, index) => (
            <span className="h-1 w-1 rounded-full bg-[#efd8bf]" key={index} />
          ))}
        </div>
        <div className="pointer-events-none absolute right-0 top-20 hidden h-[360px] w-[360px] rounded-full border border-[#dfc7aa]/50 opacity-60 lg:block" />
        <div className="pointer-events-none absolute left-1/2 top-16 hidden -translate-x-1/2 grid-cols-7 gap-2 opacity-30 lg:grid">
          {Array.from({ length: 35 }).map((_, index) => (
            <span className="h-1 w-1 rounded-full bg-[#c8a47c]" key={index} />
          ))}
        </div>
        <div className="pointer-events-none absolute right-6 top-28 grid grid-cols-5 gap-2 opacity-30 lg:hidden">
          {Array.from({ length: 20 }).map((_, index) => (
            <span className="h-1 w-1 rounded-full bg-[#c8a47c]" key={index} />
          ))}
        </div>
        <div className="pointer-events-none absolute bottom-8 left-4 grid grid-cols-6 gap-2 opacity-20 lg:hidden">
          {Array.from({ length: 24 }).map((_, index) => (
            <span className="h-1 w-1 rounded-full bg-[#c8a47c]" key={index} />
          ))}
        </div>

        <section className="relative z-10 grid place-items-center lg:hidden">
          <div
            className={`relative grid w-full max-w-[430px] transition-all duration-500 ease-out ${visibilityClass}`}
          >
            <div className="grid gap-4 pt-3 lg:hidden">
              <div
                className={`flex flex-col items-center text-center transition-all delay-75 duration-500 ease-out ${mobileVisibilityClass}`}
              >
                <div className="flex h-16 w-16 items-center justify-center rounded-[20px] bg-white/80 text-[#6B3A1E] shadow-[0_14px_34px_rgba(75,42,24,0.1)] backdrop-blur-xl">
                  <RcmLogo />
                </div>
                <div className="mt-2 font-serif text-[2.5rem] leading-none tracking-[-0.03em] text-[#3B1F12]">
                  RCM
                </div>
                <div className="mt-0.5 text-[0.55rem] font-semibold uppercase tracking-[0.5em] text-[#9A7A4F]">
                  Ventures
                </div>
                <p className="mt-3 text-[1.05rem] font-semibold text-slate-800">
                  Business Portal
                </p>
                <p className="mt-1 max-w-xs text-[0.72rem] leading-5 text-slate-500">
                  Secure access to your operations
                </p>
              </div>

              <div className="grid w-full grid-cols-3 gap-2">
                {mobileFeatureCards.map(({ label, detail, icon: Icon }) => (
                  <div
                    className={`flex h-[74px] flex-col items-center justify-center rounded-2xl bg-white/80 p-2 shadow-[0_10px_24px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all duration-500 ease-out ${mobileVisibilityClass}`}
                    key={label}
                  >
                    <div className="mb-1 flex h-8 w-8 items-center justify-center rounded-xl bg-[#f3eadf]">
                      <Icon className="h-4 w-4 text-[#6B3A1E]" aria-hidden="true" />
                    </div>
                    <p className="text-center text-[0.65rem] font-semibold leading-tight text-slate-900">
                      {label}
                    </p>
                    <p className="mt-0.5 text-center text-[0.55rem] leading-tight text-slate-500">
                      {detail}
                    </p>
                  </div>
                ))}
              </div>

              <form
                className={`w-full space-y-4 rounded-[26px] bg-white/90 p-5 shadow-[0_18px_44px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all delay-150 duration-500 ease-out ${mobileVisibilityClass}`}
                onSubmit={handleSubmit}
              >
                <div>
                  <p className="text-[0.62rem] font-semibold uppercase tracking-[0.28em] text-[#6B3A1E]">
                    {heading}
                  </p>
                  <h2 className="mt-3 text-[1.45rem] font-bold tracking-[-0.05em] text-slate-900">
                    {portal === 'admin' ? (
                      <>
                        Welcome back <span aria-hidden="true">👋</span>
                      </>
                    ) : (
                      'Secure sign in'
                    )}
                  </h2>
                  <p className="mt-2 text-[0.78rem] leading-5 text-slate-500">
                    {portal === 'admin'
                      ? 'Sign in to continue to the administration dashboard.'
                      : 'Enter your credentials to continue to administration.'}
                  </p>
                </div>

                <div className="grid gap-3.5">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-800" htmlFor="mobile-username">
                      Username
                    </label>
                    <div className="relative">
                      <User
                        aria-hidden="true"
                        className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                      />
                      <input
                        className={mobileInputClass}
                        id="mobile-username"
                        name="username"
                        onChange={handleChange}
                        placeholder="Enter username"
                        required
                        type="text"
                        value={form.username}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-800" htmlFor="mobile-password">
                      Password
                    </label>
                    <div className="relative">
                      <Lock
                        aria-hidden="true"
                        className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                      />
                      <input
                        className={`${mobileInputClass} pr-12`}
                        id="mobile-password"
                        name="password"
                        onChange={handleChange}
                        placeholder="Enter password"
                        required
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                      />
                      {renderMobilePasswordToggle()}
                    </div>
                  </div>

                  <button
                    className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#3B1F12] to-[#6B3A1E] text-lg font-semibold text-white shadow-lg transition-all duration-300 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                    disabled={loading}
                    type="submit"
                  >
                    <span>{loading ? 'Signing in...' : 'Login'}</span>
                    <ArrowRight className="h-5 w-5" aria-hidden="true" />
                  </button>

                  {sessionMessage ? (
                    <p className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900">
                      {sessionMessage}
                    </p>
                  ) : null}

                  {error ? (
                    <p className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
                      {error}
                    </p>
                  ) : null}
                </div>
              </form>
            </div>
          </div>
        </section>

        <section className={`relative z-10 hidden w-full max-w-[1080px] grid-cols-2 items-center gap-12 transition-all duration-700 ease-out lg:grid ${visibilityClass}`}>
          <div className="relative flex min-h-[540px] flex-col justify-center">
            <div className="pointer-events-none absolute -left-28 top-52 z-0 h-72 w-72 opacity-[0.045]">
              <div className="absolute bottom-0 left-6 h-40 w-16 rounded-t-[22px] bg-[#6B3A1E]" />
              <div className="absolute bottom-0 left-20 h-56 w-20 rounded-t-[26px] bg-[#6B3A1E]" />
              <div className="absolute bottom-0 left-36 h-32 w-24 rounded-t-[20px] bg-[#6B3A1E]" />
              <div className="absolute bottom-0 left-56 h-44 w-14 rounded-t-[18px] bg-[#6B3A1E]" />
              <div className="absolute left-12 top-24 grid grid-cols-2 gap-3">
                {Array.from({ length: 10 }).map((_, index) => (
                  <span className="h-2 w-2 rounded-full bg-white" key={index} />
                ))}
              </div>
              <div className="absolute left-28 top-10 grid grid-cols-2 gap-3">
                {Array.from({ length: 14 }).map((_, index) => (
                  <span className="h-2 w-2 rounded-full bg-white" key={index} />
                ))}
              </div>
              <div className="absolute bottom-0 left-0 h-1 w-72 rounded-full bg-[#6B3A1E]" />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-[22px] bg-white/80 text-[#6B3A1E] shadow-[0_16px_38px_rgba(75,42,24,0.1)] backdrop-blur-xl">
                <RcmLogo />
              </div>
              <div>
                <div className="font-serif text-[2.9rem] leading-none tracking-[-0.03em] text-[#3B1F12]">
                  RCM
                </div>
                <div className="mt-1 text-[0.62rem] font-semibold uppercase tracking-[0.5em] text-[#9A7A4F]">
                  Ventures
                </div>
              </div>
            </div>

            <div className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-[#f1e4d5] px-3.5 py-1.5 text-[0.68rem] font-bold uppercase tracking-widest text-[#5A321E]">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              Business Portal
            </div>

            <h1 className="mt-5 max-w-xl text-[2.45rem] font-bold leading-[1.12] tracking-tight text-[#1f2937]">
              Control access with a <span className="font-extrabold text-[#6B3A1E]">smarter</span> operating desk.
            </h1>
            <p className="mt-4 max-w-xl text-base leading-7 text-slate-600">
              Review tenant access, manage users, and coordinate operations across all branches from one secure platform.
            </p>

            <div className="mt-5 max-w-[500px] space-y-2.5">
              {desktopFeatureCards.map(({ title, description, icon: Icon }) => (
                <div
                  className="group flex items-center gap-4 rounded-2xl border border-white/70 bg-white/85 p-3.5 shadow-[0_14px_34px_rgba(15,23,42,0.07)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_48px_rgba(15,23,42,0.12)]"
                  key={title}
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#f3eadf] text-[#6B3A1E]">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
                    <p className="mt-0.5 text-xs leading-5 text-slate-500">{description}</p>
                  </div>
                  <ArrowRight className="ml-auto h-4 w-4 text-[#6B3A1E] opacity-70 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </div>
              ))}
            </div>

            <div className="mt-4 inline-flex w-fit items-center gap-2 rounded-2xl bg-white/80 px-4 py-2.5 text-xs font-medium text-[#5A321E] shadow-[0_14px_34px_rgba(75,42,24,0.08)]">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              Secure • Reliable • Scalable • Always Available
            </div>
          </div>

          <div className="relative">
            <form
              className="relative ml-auto max-w-[430px] rounded-[30px] border border-white/70 bg-white/90 px-8 pb-11 pt-8 shadow-[0_24px_70px_rgba(15,23,42,0.12)] backdrop-blur-xl"
              onSubmit={handleSubmit}
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#eadccf] bg-[#f3eadf] text-[#5A321E]">
                <ShieldCheck className="h-5 w-5" aria-hidden="true" />
              </div>
              <p className="mt-5 text-[0.68rem] font-bold uppercase tracking-[0.35em] text-[#5A321E]">
                {heading}
              </p>
              <h2 className="mt-3 text-[1.85rem] font-extrabold tracking-tight text-slate-900">
                {portal === 'admin' ? (
                  <>
                    Welcome back <span aria-hidden="true">👋</span>
                  </>
                ) : (
                  'Secure sign in'
                )}
              </h2>
              <p className="mt-2 text-[0.84rem] leading-5 text-slate-500">
                {portal === 'admin'
                  ? 'Sign in to continue to the administration dashboard.'
                  : 'Enter your credentials to continue to administration.'}
              </p>

              <div className="mt-5 space-y-3.5">
                <div>
                  <label className="mb-1.5 block text-sm font-bold text-slate-800" htmlFor="desktop-username">
                    Username
                  </label>
                  <div className="relative">
                    <User className="absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                    <input
                      className="h-12 w-full rounded-2xl border border-[#ded4c8] bg-white pl-[52px] pr-4 text-slate-900 outline-none transition-all duration-300 placeholder:text-slate-400 focus:border-[#6B3A1E] focus:ring-4 focus:ring-[#eadccf]"
                      id="desktop-username"
                      name="username"
                      onChange={handleChange}
                      placeholder="Enter username"
                      required
                      type="text"
                      value={form.username}
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-bold text-slate-800" htmlFor="desktop-password">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                    <input
                      className="h-12 w-full rounded-2xl border border-[#ded4c8] bg-white pl-[52px] pr-[52px] text-slate-900 outline-none transition-all duration-300 placeholder:text-slate-400 focus:border-[#6B3A1E] focus:ring-4 focus:ring-[#eadccf]"
                      id="desktop-password"
                      name="password"
                      onChange={handleChange}
                      placeholder="Enter password"
                      required
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                    />
                    <button
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-[#6B3A1E]"
                      onClick={() => setShowPassword((currentValue) => !currentValue)}
                      type="button"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" aria-hidden="true" />
                      ) : (
                        <Eye className="h-5 w-5" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  className="flex h-12 w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-[#3B1F12] to-[#6B3A1E] text-base font-bold text-white shadow-[0_16px_30px_rgba(75,42,24,0.25)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_40px_rgba(75,42,24,0.35)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                  disabled={loading}
                  type="submit"
                >
                  <span>{loading ? 'Signing in...' : 'Login'}</span>
                  <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </button>

                {sessionMessage ? (
                  <p className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900">
                    {sessionMessage}
                  </p>
                ) : null}

                {error ? (
                  <p className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
                    {error}
                  </p>
                ) : null}
              </div>
            </form>
          </div>
        </section>

        <div
          className={`relative z-10 mt-10 text-center text-[0.72rem] font-semibold text-slate-500 transition-all delay-100 duration-500 ease-out lg:absolute lg:bottom-4 lg:left-1/2 lg:mt-0 lg:flex lg:-translate-x-1/2 lg:items-center lg:gap-3 lg:text-sm ${visibilityClass}`}
        >
          <span className="inline-flex items-center justify-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#6B3A1E]" aria-hidden="true" />
            <a
              href="https://zerooneitinc.com/"
              target="_blank"
              rel="noreferrer"
              className="transition-colors hover:text-[#6B3A1E]"
            >
              © 2026 ZeroOne IT Inc.
            </a>
          </span>
          <span className="mx-2 hidden sm:inline">|</span>
          <span className="block sm:inline">All rights reserved</span>
        </div>
      </main>
    </div>
  );
}

export default LoginPage;
